import React from 'react'
import { Calendar, Clock, Users, Save, RefreshCw, Copy } from 'lucide-react'
import { Button } from '../ui'
import { useThemeTokens } from '../../themes'

interface TimesheetStatusBarProps {
  currentDate: string
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'empty'
  lastSavedAt?: string | null
  draftWorkerCount?: number
  totalHours?: number
  onLoadDraft?: () => void
  onStartFresh?: () => void
  suggestedCopyDate?: string
  onCopyYesterday?: () => void
  savingDraft?: boolean
  className?: string
}

export const TimesheetStatusBar: React.FC<TimesheetStatusBarProps> = ({
  currentDate,
  status,
  lastSavedAt,
  draftWorkerCount,
  totalHours,
  onLoadDraft,
  onStartFresh,
  suggestedCopyDate,
  onCopyYesterday,
  savingDraft = false,
  className
}) => {
  const theme = useThemeTokens()

  // Format date for display
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  // Format time for display
  const formatTime = (dateTimeString: string): string => {
    const date = new Date(dateTimeString)
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  // Get status display info
  const getStatusInfo = () => {
    switch (status) {
      case 'draft':
        return {
          icon: Save,
          text: lastSavedAt 
            ? `💾 Draft available from ${formatTime(lastSavedAt)}`
            : '💾 Draft in progress',
          color: theme.colors.warning,
          details: draftWorkerCount 
            ? `${draftWorkerCount} workers, ${totalHours?.toFixed(1) || '0.0'} hours`
            : 'No workers added yet'
        }
      case 'submitted':
        return {
          icon: Clock,
          text: '✅ Submitted',
          color: theme.colors.success,
          details: draftWorkerCount 
            ? `${draftWorkerCount} workers, ${totalHours?.toFixed(1) || '0.0'} hours`
            : ''
        }
      case 'approved':
        return {
          icon: Clock,
          text: '✅ Approved',
          color: theme.colors.success,
          details: draftWorkerCount 
            ? `${draftWorkerCount} workers, ${totalHours?.toFixed(1) || '0.0'} hours`
            : ''
        }
      case 'rejected':
        return {
          icon: Clock,
          text: '❌ Rejected',
          color: theme.colors.error,
          details: draftWorkerCount 
            ? `${draftWorkerCount} workers, ${totalHours?.toFixed(1) || '0.0'} hours`
            : ''
        }
      default:
        return {
          icon: Calendar,
          text: '📅 Ready to start',
          color: theme.colors.textSecondary,
          details: suggestedCopyDate 
            ? `💡 Yesterday: workers available → Copy to today`
            : 'Add workers to begin'
        }
    }
  }

  const statusInfo = getStatusInfo()
  const StatusIcon = statusInfo.icon

  // Container styles
  const containerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.md,
    border: `1px solid ${theme.colors.border}`,
    marginBottom: theme.spacing.lg,
    flexWrap: 'wrap',
    gap: theme.spacing.md
  }

  // Status info section styles
  const statusSectionStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.sm,
    flex: 1,
    minWidth: '300px'
  }

  // Header styles
  const headerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.md,
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.text
  }

  // Status display styles
  const statusDisplayStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    fontSize: theme.typography.fontSize.md,
    color: statusInfo.color
  }

  // Details styles
  const detailsStyles: React.CSSProperties = {
    fontSize: theme.typography.fontSize.sm,
    color: theme.colors.textSecondary,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm
  }

  // Actions section styles
  const actionsStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    flexWrap: 'wrap'
  }

  return (
    <div style={containerStyles} className={className}>
      {/* Status Information */}
      <div style={statusSectionStyles}>
        {/* Date Header */}
        <div style={headerStyles}>
          <Calendar size={24} color={theme.colors.primary} />
          <span>{formatDate(currentDate)}</span>
        </div>

        {/* Status Display */}
        <div style={statusDisplayStyles}>
          <StatusIcon size={16} />
          <span>{statusInfo.text}</span>
        </div>

        {/* Details */}
        {statusInfo.details && (
          <div style={detailsStyles}>
            {draftWorkerCount && draftWorkerCount > 0 ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
                  <Users size={14} />
                  <span>{draftWorkerCount} worker{draftWorkerCount !== 1 ? 's' : ''}</span>
                </div>
                <span>•</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
                  <Clock size={14} />
                  <span>{totalHours?.toFixed(1) || '0.0'} hours</span>
                </div>
              </>
            ) : (
              <span>{statusInfo.details}</span>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={actionsStyles}>
        {status === 'draft' && lastSavedAt && onLoadDraft && onStartFresh && (
          <>
            <Button
              variant="primary"
              size="sm"
              onClick={onLoadDraft}
              disabled={savingDraft}
            >
              {savingDraft ? (
                <>
                  <RefreshCw size={14} className="animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Save size={14} />
                  Load Draft
                </>
              )}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={onStartFresh}
              disabled={savingDraft}
            >
              <RefreshCw size={14} />
              Start Fresh
            </Button>
          </>
        )}

        {status === 'empty' && suggestedCopyDate && onCopyYesterday && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onCopyYesterday}
          >
            <Copy size={14} />
            Copy from {suggestedCopyDate}
          </Button>
        )}

        {status === 'submitted' && (
          <Button
            variant="ghost"
            size="sm"
            disabled
          >
            View Only
          </Button>
        )}

        {status === 'approved' && (
          <Button
            variant="ghost"
            size="sm"
            disabled
          >
            Approved
          </Button>
        )}
      </div>
    </div>
  )
} 